// ============================================================================================
// NOVOS PRODUTOS — a prateleira do item que ainda nao e de ninguem (DA-606)
// ============================================================================================
// Item que o Ruan quer COTAR mas a loja ainda nao vende, e que nenhum fornecedor cotou. Nao cabe em
// `produtos` (cadastro comercial/fiscal) nem no Catalogo (que exige fornecedor). Vive aqui ate a
// primeira compra: o XML da nota casa com ele, ele vira `comprado` e a identidade migra pro
// Catalogo daquele fornecedor.
//
// ⛔ Nao ha atalho de cadastro na cotacao — decisao do Ruan (22/08/2026): "tudo nasce na tabela de
// Novos_Produtos". Esta tela e a UNICA porta de entrada.

import { useCallback, useEffect, useRef, useState } from 'react';
import { upperMantendoCursor } from '../../components/inputs/UpperInput';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, Search, PackagePlus, Trash2, RotateCcw, ImageOff, X, Upload, Copy } from 'lucide-react';
import { api, ApiError } from '../../lib/api';
import { useToast } from '../../components/Toast';
import { useDialog } from '../../components/DialogProvider';
import { Modal } from '../../components/Modal';
import { UpperInput } from '../../components/inputs/UpperInput';
import { ComboBox, type OpcaoCombo } from '../../components/ComboBox';
import { ModalCadastroRapido } from '../../components/ModalCadastroRapido';
import { FotoProduto } from '../../components/FotoProduto';
import { useGridNavegavel } from '../../lib/useGridNavegavel';
import { useStickyHeader } from '../../lib/useStickyHeader';
import { formatDataSP, formatMoeda, formatMoedaSemSimbolo, parseMoeda, getNomeFornecedor } from '@fiodecobre/shared';
// O tipo e o preenchimento do formulario moram em peca propria desde o DUPLICAR ([DA-608]):
// dois enchedores (Editar e Duplicar) pedem uma funcao so, testada.
import { VAZIO, formDoItem, formIgual, parseQtd, fmtQtd, type NovoProduto } from './form-novo-produto';

export type { NovoProduto };

type Parecido = { onde: 'produto' | 'catalogo' | 'novo_produto'; id: number; codigo: string | null; descricao: string; empresa: string | null };

const MIMES_FOTO = ['image/jpeg', 'image/png', 'image/webp'];

/**
 * A foto do original virando dataURL pra viajar na COPIA ([DA-608]). Ela sobe pelo MESMO cano do
 * Ctrl+V (`POST /:id/foto`), e nao por uma rota nova que copiasse o arquivo no servidor: assim a
 * copia ganha arquivo PROPRIO, e apagar a foto de uma nunca apaga a da outra — que e' o que
 * aconteceria se as duas linhas apontassem pra mesma `foto_url` (o `apagarFotoAnterior` do
 * backend faz `unlink` no arquivo). De quebra o recurso inteiro fica sem backend, entao o deploy
 * nao reinicia o servico.
 */
async function baixarFotoComoDataUrl(url: string): Promise<{ dataUrl: string; mime: string }> {
  const r = await fetch(url);
  if (!r.ok) throw new Error(`foto ${r.status}`);
  const blob = await r.blob();
  // O servidor grava tudo como WebP; se o Content-Type vier estranho, o zod da rota recusaria.
  const mime = MIMES_FOTO.includes(blob.type) ? blob.type : 'image/webp';
  const dataUrl = await new Promise<string>((resolve, reject) => {
    const fr = new FileReader();
    fr.onload = () => resolve(String(fr.result));
    fr.onerror = reject;
    fr.readAsDataURL(blob);
  });
  return { dataUrl, mime };
}

// As tres vistas se EXCLUEM (DA-597): checkbox combinavel daria quatro estados pra uma tela que
// tem tres, e o numero ao lado de um mudaria conforme o outro.
const VISTAS = [
  { chave: 'ativo', rotulo: 'Na prateleira' },
  { chave: 'comprado', rotulo: 'Comprados' },
  { chave: 'descartado', rotulo: 'Descartados' },
] as const;
type Vista = (typeof VISTAS)[number]['chave'];

export default function NovosProdutos() {
  const navigate = useNavigate();
  const toast = useToast();
  const dialog = useDialog();
  const dialogRef = useRef(dialog);
  useEffect(() => { dialogRef.current = dialog; }, [dialog]);
  const sticky = useStickyHeader();

  const [itens, setItens] = useState<NovoProduto[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [busca, setBusca] = useState('');
  const [vista, setVista] = useState<Vista>('ativo');

  const [modalAberto, setModalAberto] = useState(false);
  const [editando, setEditando] = useState<NovoProduto | null>(null);
  // De qual item esta copia saiu ([DA-608]). Nao e' vinculo: serve pra tela DIZER o que vai
  // acontecer ao salvar — nasce item novo, com codigo proprio, e o original fica como esta.
  const [duplicandoDe, setDuplicandoDe] = useState<NovoProduto | null>(null);
  const [form, setForm] = useState({ ...VAZIO });
  const [salvando, setSalvando] = useState(false);
  const [parecidos, setParecidos] = useState<Parecido[]>([]);

  // Marca, Unidade e Embalagem se ESCOLHEM de cadastro — nao se digitam (convencao de UI do
  // CLAUDE.md raiz). Mesmas listas e mesmo desenho do modal do Catalogo do Fornecedor, que e a
  // prateleira irma: campo digitado livre fabrica sinonimo ("VONDER" x "VONDER FERRAMENTAS") e
  // ninguem delata a divergencia depois ([DA-604]).
  const [marcas, setMarcas] = useState<{ id: number; nome: string }[]>([]);
  const [unidades, setUnidades] = useState<{ id: number; sigla: string; nome: string; ativo?: boolean }[]>([]);
  const [mostrarNovaMarca, setMostrarNovaMarca] = useState(false);
  // EMPRESA, nao filial: o codigo do fornecedor e o mesmo em todas as inscricoes dela, e e a
  // empresa que o Catalogo amarra desde a [DA-311].
  const [fornecedores, setFornecedores] = useState<{ id: number; nome_fantasia: string | null; razao_social?: string | null }[]>([]);

  useEffect(() => {
    api.get<{ id: number; nome: string }[]>('/api/marcas').then(setMarcas).catch(() => {});
    api.get<{ id: number; sigla: string; nome: string }[]>('/api/unidades-medida').then(setUnidades).catch(() => {});
    api.get<{ id: number; nome_fantasia: string | null; razao_social?: string | null }[]>(
      '/api/fornecedores', { query: { todos: true } },
    ).then(setFornecedores).catch(() => {});
  }, []);

  // O valor que ja esta gravado entra na lista mesmo sem cadastro, marcado. Sumir com ele faria o
  // combo aparecer VAZIO num item que tem marca — e salvar apagaria o dado sem ninguem pedir.
  function comAtual(opcoes: OpcaoCombo[], atual: string): OpcaoCombo[] {
    if (!atual || opcoes.some((o) => String(o.value) === atual)) return opcoes;
    return [{ value: atual, label: `${atual} (nao cadastrada)` }, ...opcoes];
  }
  const opcoesMarca = comAtual(marcas.map((m) => ({ value: m.nome, label: m.nome })), form.marca);
  const opcoesUnidade = (atual: string) =>
    // Inativa (migration 0311) so entra se for a que o item ja usa, marcada.
    comAtual(unidades
      .filter((u) => u.ativo !== false || u.sigla === atual)
      .map((u) => ({ value: u.sigla, label: `${u.sigla} — ${u.nome}${u.ativo === false ? ' (inativa)' : ''}` })), atual);
  const opcoesFornecedor = fornecedores.map((f) => ({ value: f.id, label: getNomeFornecedor(f) }));

  // O UNITARIO nao e digitado: sai da divisao, como no modal do Catalogo. Dois campos editaveis
  // pro mesmo numero divergem e ninguem sabe qual manda ([DA-600] p.2).
  const unitarioPrevia = (() => {
    const p = parseMoeda(form.preco_embalagem);
    // `parseQtd` e' a MESMA leitura que o salvar usa — ler a quantidade de um jeito aqui e de
    // outro na gravacao e' o que fazia esta caixinha discordar da lista ([DA-608] p.2).
    const q = parseQtd(form.quantidade_embalagem);
    return p > 0 && q != null && q > 0 ? p / q : null;
  })();

  // ------------------------------------------------------------------------------------------
  // A foto DENTRO do modal — mesmo campo do Monitor de Custos (`ModalFotoOferta`): area tracejada
  // que aceita Ctrl+V, arrastar e clicar pra escolher arquivo. Sem esteira de foto de produto
  // (recorte, padrao Master): aqui a foto so IDENTIFICA o item, e nao viaja pro Catalogo nem pro
  // cadastro quando ele for comprado ([DA-606]).
  // ⚠️ No cadastro o item ainda NAO TEM id, entao a imagem fica em mao e sobe logo depois do POST.
  // ------------------------------------------------------------------------------------------
  const [fotoNova, setFotoNova] = useState<string | null>(null); // dataURL ainda nao enviado
  const [fotoMime, setFotoMime] = useState('image/png');
  const [fotoApagar, setFotoApagar] = useState(false); // edicao: tirar a que ja esta la
  const [copiandoFoto, setCopiandoFoto] = useState(false); // duplicata: baixando a foto do original
  const arquivoRef = useRef<HTMLInputElement>(null);
  // Mesma guarda de corrida da lista: abrir outra copia (ou um item em branco) enquanto o
  // download anda faria a foto do item anterior pousar no formulario errado.
  const fotoReq = useRef(0);

  const copiarFotoDoOriginal = useCallback(async (url: string) => {
    const req = ++fotoReq.current;
    setCopiandoFoto(true);
    try {
      const { dataUrl, mime } = await baixarFotoComoDataUrl(url);
      if (req !== fotoReq.current) return;
      setFotoNova(dataUrl); setFotoMime(mime); setFotoApagar(false);
    } catch {
      // Falhar aqui nao pode atrapalhar a copia: o resto do registro ja esta na tela.
      if (req === fotoReq.current) toast.aviso('A foto do original nao veio junto — cole a do item com Ctrl+V.');
    } finally {
      if (req === fotoReq.current) setCopiandoFoto(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const aceitarFoto = useCallback((f: File | null | undefined) => {
    if (!f) return;
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(f.type)) { toast.erro('So JPG, PNG ou WebP'); return; }
    if (f.size > 8 * 1024 * 1024) { toast.erro('Imagem maior que 8MB'); return; }
    const r = new FileReader();
    r.onload = () => { setFotoNova(String(r.result)); setFotoMime(f.type); setFotoApagar(false); };
    r.readAsDataURL(f);
    // `toast` fora das deps: o provider recria o objeto a cada render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Ctrl+V com o modal ABERTO. O listener da lista (mais abaixo) desiste quando `modalAberto`,
  // entao os dois nunca disputam a mesma colagem.
  useEffect(() => {
    if (!modalAberto) return;
    function onPaste(e: ClipboardEvent) {
      for (const it of e.clipboardData?.items ?? []) {
        if (it.type.startsWith('image/')) {
          const f = it.getAsFile();
          if (f) { e.preventDefault(); aceitarFoto(f); return; }
        }
      }
    }
    document.addEventListener('paste', onPaste);
    return () => document.removeEventListener('paste', onPaste);
  }, [modalAberto, aceitarFoto]);

  // O que a area MOSTRA: a imagem nova ganha da que ja esta gravada; pedir pra apagar mostra o vazio.
  const fotoMostrando = fotoNova ?? (fotoApagar ? null : editando?.foto_url ?? null);

  // ⚠️ Guarda contra corrida: a busca e debounced e as vistas trocam na hora — sem isto a resposta
  // da consulta mais LENTA (a sem filtro) sobrescreve a da mais nova, e a tela fica coerentemente
  // errada (o rotulo diz uma coisa e a lista mostra outra).
  const reqId = useRef(0);

  const carregar = useCallback(async () => {
    const req = ++reqId.current;
    setCarregando(true);
    try {
      const d = await api.get<NovoProduto[]>('/api/novos-produtos', {
        query: { status: vista, q: busca },
      });
      if (req !== reqId.current) return;
      setItens(Array.isArray(d) ? d : []);
    } catch (e: unknown) {
      if (req !== reqId.current) return;
      toast.erro(e instanceof ApiError ? e.message : 'Erro ao carregar');
    } finally {
      if (req === reqId.current) setCarregando(false);
    }
    // `toast` fica fora das deps de proposito: o provider recria o objeto a cada render e a lista
    // recarregaria a cada confirmacao aberta na tela.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vista, busca]);

  useEffect(() => {
    const t = setTimeout(() => { void carregar(); }, busca ? 300 : 0);
    return () => clearTimeout(t);
  }, [carregar, busca]);

  function fecharModal() {
    fotoReq.current++; // download de foto em voo nao pode pousar no proximo formulario
    setModalAberto(false);
    setDuplicandoDe(null);
    setCopiandoFoto(false);
  }

  function abrirNovo() {
    fotoReq.current++;
    setEditando(null); setDuplicandoDe(null);
    setForm({ ...VAZIO });
    setParecidos([]);
    setFotoNova(null); setFotoApagar(false); setCopiandoFoto(false);
    setModalAberto(true);
  }

  function abrirEdicao(item: NovoProduto) {
    if (item.status !== 'ativo') {
      toast.aviso('Item que ja saiu da prateleira nao se edita.');
      return;
    }
    fotoReq.current++;
    setEditando(item); setDuplicandoDe(null);
    setForm(formDoItem(item));
    setParecidos([]);
    setFotoNova(null); setFotoApagar(false); setCopiandoFoto(false);
    setModalAberto(true);
  }

  /**
   * DUPLICAR ([DA-608]) — abre o modal como item NOVO ja preenchido, nunca gravando uma copia
   * exata por conta propria: item repetido na prateleira e' justamente o que a tela combate
   * (o aviso de sosia, [DA-604]). O codigo NP so nasce no Salvar, entao desistir nao deixa nada.
   */
  function abrirDuplicata(item: NovoProduto) {
    fotoReq.current++;
    setEditando(null); setDuplicandoDe(item);
    setForm(formDoItem(item));
    setParecidos([]);
    setFotoNova(null); setFotoApagar(false); setCopiandoFoto(false);
    setModalAberto(true);
    if (item.foto_url) void copiarFotoDoOriginal(item.foto_url);
  }

  /**
   * O mesmo Duplicar a partir do modal ABERTO — leva o que esta na tela, inclusive o que ele
   * acabou de digitar e ainda nao salvou. Como isso deixaria a edicao do original pra tras em
   * silencio, quando ha alteracao pendente a tela pergunta antes ([DA-408]: campo que aceita a
   * digitacao e joga fora calado e' pior que campo travado).
   */
  async function duplicarDoModal() {
    const origem = editando;
    if (!origem) return;
    if (!formIgual(form, formDoItem(origem))) {
      const ok = await dialogRef.current.confirmar(
        `Voce alterou campos de ${origem.codigo} e ainda nao salvou.\n\n` +
        `Duplicar leva o que esta na tela pra um item NOVO — o ${origem.codigo} continua como esta.`,
      );
      if (!ok) return;
    }
    const trazerFoto = !fotoNova && !fotoApagar && !!origem.foto_url;
    setEditando(null); setDuplicandoDe(origem);
    setParecidos([]);
    setFotoApagar(false); // sem original por tras, "apagar" nao teria o que apagar
    if (trazerFoto) void copiarFotoDoOriginal(origem.foto_url as string);
  }

  const grid = useGridNavegavel<NovoProduto>({
    itens,
    getId: (i) => i.id,
    onAtivar: (i) => abrirEdicao(i),
  });

  // O aviso de SOSIA — a defesa contra "dois nomes pra mesma coisa" (familia DA-591/DA-604).
  // AVISA, nao trava: produto parecido de verdade existe, e travar ja custou caro (DA-462).
  async function conferirParecidos(descricao: string) {
    const d = descricao.trim();
    if (d.length < 3) { setParecidos([]); return; }
    try {
      const r = await api.get<Parecido[]>('/api/novos-produtos/parecidos', { query: { descricao: d } });
      setParecidos((Array.isArray(r) ? r : []).filter((p) => !(p.onde === 'novo_produto' && p.id === editando?.id)));
    } catch { /* aviso e cortesia: falhar aqui nao pode atrapalhar o cadastro */ }
  }

  async function salvar() {
    if (!form.descricao.trim()) { toast.erro('A descricao e obrigatoria.'); return; }
    setSalvando(true);
    try {
      const corpo = {
        descricao: form.descricao,
        marca: form.marca || null,
        embalagem: form.embalagem || null,
        // Numero, nunca o texto do campo: "1.000" chega no Postgres como UM ([DA-608] p.2).
        quantidade_embalagem: parseQtd(form.quantidade_embalagem),
        ncm: form.ncm || null,
        observacao: form.observacao || null,
        fornecedor_id: form.fornecedor_id === '' ? null : Number(form.fornecedor_id),
        codigo_fornecedor: form.codigo_fornecedor || null,
        preco_embalagem: form.preco_embalagem || null,
      };
      let alvoId: number | null = null;
      if (editando) {
        await api.put(`/api/novos-produtos/${editando.id}`, corpo);
        alvoId = editando.id;
        toast.sucesso('Item atualizado');
      } else {
        const criado = await api.post<NovoProduto>('/api/novos-produtos', corpo);
        alvoId = criado?.id ?? null;
        toast.sucesso(`${duplicandoDe ? 'Copia cadastrada' : 'Cadastrado'} como ${criado?.codigo ?? 'novo item'}`);
      }

      // ⛔ A foto vem DEPOIS e falha SOZINHA ([DA-559]): o item ja esta gravado, entao um erro
      // aqui nao pode ler como "nao salvou" — quem clicasse de novo cadastraria o item duas
      // vezes. Estado proprio na tela, e a porta de reparo e a de sempre: selecionar a linha e
      // colar com Ctrl+V, que sobrescreve e portanto pode ser repetida a vontade.
      if (alvoId) {
        try {
          if (fotoNova) await api.post(`/api/novos-produtos/${alvoId}/foto`, { base64: fotoNova, mime: fotoMime });
          else if (fotoApagar) await api.delete(`/api/novos-produtos/${alvoId}/foto`);
        } catch {
          toast.aviso('O item foi salvo, mas a FOTO nao subiu — selecione a linha na lista e cole com Ctrl+V.');
        }
      }

      fecharModal();
      await carregar();
    } catch (e: unknown) {
      toast.erro(e instanceof ApiError ? e.message : 'Erro ao salvar');
    } finally {
      setSalvando(false);
    }
  }

  async function descartar(item: NovoProduto) {
    const ok = await dialogRef.current.confirmar(
      `Descartar ${item.codigo} — ${item.descricao}?\n\nEle sai da prateleira e da busca da cotacao. Da pra trazer de volta depois.`,
      { perigoso: true },
    );
    if (!ok) return;
    try {
      await api.post(`/api/novos-produtos/${item.id}/descartar`, {});
      reqId.current++; // a acao ja gravou: invalida carga em voo
      await carregar();
      toast.sucesso('Descartado');
    } catch (e: unknown) {
      toast.erro(e instanceof ApiError ? e.message : 'Erro ao descartar');
    }
  }

  async function reativar(item: NovoProduto) {
    try {
      await api.post(`/api/novos-produtos/${item.id}/reativar`, {});
      reqId.current++;
      await carregar();
      toast.sucesso('De volta na prateleira');
    } catch (e: unknown) {
      toast.erro(e instanceof ApiError ? e.message : 'Erro ao reativar');
    }
  }

  // ------------------------------------------------------------------------------------------
  // Foto colada (Ctrl+V) — mesmo contrato do Monitor (DA-302): JSON com dataURL, nao FormData.
  // O listener vive no `document` enquanto a linha estiver selecionada, como no ModalFotoOferta.
  // ------------------------------------------------------------------------------------------
  const selecionado = itens.find((i) => i.id === grid.selectedId) ?? null;
  const selecionadoRef = useRef<NovoProduto | null>(null);
  useEffect(() => { selecionadoRef.current = selecionado; }, [selecionado]);

  useEffect(() => {
    async function onPaste(e: ClipboardEvent) {
      const alvo = selecionadoRef.current;
      if (!alvo || alvo.status !== 'ativo' || modalAberto) return;
      const item = Array.from(e.clipboardData?.items ?? []).find((i) => i.type.startsWith('image/'));
      if (!item) return;
      const file = item.getAsFile();
      if (!file) return;
      if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
        toast.erro('Formato nao suportado — cole JPG, PNG ou WebP.');
        return;
      }
      if (file.size > 8 * 1024 * 1024) { toast.erro('Imagem maior que 8MB.'); return; }
      e.preventDefault();
      const base64 = await new Promise<string>((resolve, reject) => {
        const r = new FileReader();
        r.onload = () => resolve(String(r.result));
        r.onerror = reject;
        r.readAsDataURL(file);
      });
      try {
        await api.post(`/api/novos-produtos/${alvo.id}/foto`, { base64, mime: file.type });
        reqId.current++;
        await carregar();
        toast.sucesso('Foto colada');
      } catch (err: unknown) {
        toast.erro(err instanceof ApiError ? err.message : 'Erro ao salvar a foto');
      }
    }
    const handler = (e: Event) => { void onPaste(e as ClipboardEvent); };
    document.addEventListener('paste', handler);
    return () => document.removeEventListener('paste', handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [carregar, modalAberto]);

  async function tirarFoto(item: NovoProduto) {
    try {
      await api.delete(`/api/novos-produtos/${item.id}/foto`);
      reqId.current++;
      await carregar();
    } catch (e: unknown) {
      toast.erro(e instanceof ApiError ? e.message : 'Erro ao remover a foto');
    }
  }

  const th: React.CSSProperties = { padding: '8px 10px', textAlign: 'left', whiteSpace: 'nowrap' };
  const td: React.CSSProperties = { padding: '6px 10px', verticalAlign: 'middle' };

  return (
    <div>
      <div ref={sticky.headerRef} style={sticky.headerStyle}>
        <div className="pagina-header">
          <h2 className="pagina-titulo" style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            <button type="button" className="btn-icon" onClick={() => navigate(-1)} title="Voltar">
              <ArrowLeft size={20} />
            </button>
            <PackagePlus size={20} style={{ display: 'inline', marginRight: 6, marginBottom: -3 }} />
            Novos Produtos
          </h2>
          <div className="pagina-acoes">
            <button type="button" className="btn-primario" onClick={abrirNovo}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap' }}>
              <Plus size={16} /> Novo item
            </button>
          </div>
        </div>

        <div style={{ position: 'relative', marginBottom: 12 }}>
          <Search size={16} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
          <input
            type="text"
            className="pl-9"
            placeholder="Buscar por descricao, marca, NCM, codigo NP ou codigo do fornecedor..."
            value={busca}
            onChange={(e) => setBusca(upperMantendoCursor(e))}
            style={{ width: '100%', border: '1px solid var(--border)', borderRadius: 8, fontSize: 13 }}
          />
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
          {VISTAS.map((v) => (
            <button
              key={v.chave}
              type="button"
              onClick={() => setVista(v.chave)}
              className={vista === v.chave ? 'btn-primario' : ''}
              style={vista === v.chave ? { padding: '5px 12px', fontSize: 12 } : {
                padding: '5px 12px', fontSize: 12, background: 'var(--panel-bg)',
                border: '1px solid var(--border)', borderRadius: 6, color: 'var(--page-fg)', cursor: 'pointer',
              }}
            >
              {v.rotulo}
            </button>
          ))}
          <span style={{ fontSize: 12, color: 'var(--text-muted)', marginLeft: 'auto' }}>
            {carregando ? 'carregando...' : `${itens.length} ${itens.length === 1 ? 'item' : 'itens'}`}
          </span>
        </div>
      </div>

      {/* overflow-x-auto, nunca hidden: coluna que passa da largura tem que continuar alcancavel */}
      <div style={{ border: '1px solid var(--border)', borderRadius: 8, overflowX: 'auto' }}>
        <table className="tabela" style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={{ ...th, width: 56 }}>FOTO</th>
              <th style={{ ...th, width: 90 }}>CODIGO</th>
              <th style={th}>DESCRICAO</th>
              <th style={{ ...th, width: 130 }}>MARCA</th>
              {/* Nao ha coluna UN. — a EMBALAGEM e' a unidade ([DA-608] p.4). */}
              <th style={{ ...th, width: 130 }}>EMBALAGEM</th>
              <th style={{ ...th, width: 150 }}>FORNECEDOR</th>
              <th style={{ ...th, width: 110, textAlign: 'right' }}>VALOR</th>
              <th style={{ ...th, width: 100 }}>NCM</th>
              <th style={{ ...th, width: 120 }}>CADASTRO</th>
              {/* 108px: sao tres icones quando o item tem foto (duplicar, tirar foto, descartar) */}
              <th style={{ ...th, width: 108, textAlign: 'center' }}>ACOES</th>
            </tr>
          </thead>
          <tbody>
            {itens.length === 0 && !carregando && (
              <tr><td colSpan={10} style={{ ...td, textAlign: 'center', color: 'var(--text-muted)', padding: 24 }}>
                {busca
                  ? `Nada encontrado para "${busca}" em ${VISTAS.find((v) => v.chave === vista)?.rotulo.toLowerCase()}.`
                  : 'A prateleira esta vazia. Use "Novo item" para cadastrar o que voce quer cotar.'}
              </td></tr>
            )}
            {itens.map((i) => (
              <tr key={i.id} {...grid.rowProps(i)}>
                <td style={td}>
                  {i.foto_url ? <FotoProduto url={i.foto_url} tam={40} /> : (
                    <div style={{ width: 40, height: 40, background: 'var(--grid-row-alt-bg)', borderRadius: 4, border: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <ImageOff size={14} color="#d1d5db" />
                    </div>
                  )}
                </td>
                <td style={{ ...td, fontWeight: 700, color: 'var(--primary)' }} className="no-underline">{i.codigo}</td>
                <td style={td}>
                  <div style={{ fontSize: 13 }}>{i.descricao}</div>
                  {i.observacao && <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{i.observacao}</div>}
                  {i.status === 'comprado' && (
                    <span style={{ fontSize: 10, fontWeight: 700, color: '#000', background: '#fde68a', borderRadius: 4, padding: '1px 6px' }}>
                      COMPRADO{i.encerrado_em ? ` · ${formatDataSP(i.encerrado_em)}` : ''}
                    </span>
                  )}
                  {i.status === 'descartado' && (
                    <span style={{ fontSize: 10, fontWeight: 700, color: '#000', background: '#94a3b8', borderRadius: 4, padding: '1px 6px' }}>
                      DESCARTADO
                    </span>
                  )}
                </td>
                <td style={td}>{i.marca || '—'}</td>
                <td style={td}>
                  {i.embalagem || '—'}
                  {i.quantidade_embalagem && <span style={{ color: 'var(--text-muted)' }}> c/ {Number(i.quantidade_embalagem).toLocaleString('pt-BR')}</span>}
                </td>
                <td style={td}>
                  {/* Fornecedor e codigo na MESMA coluna: sao uma informacao so ("onde eu vi"), e
                      duas colunas custariam 150px numa tabela que ja rola no tablet. */}
                  {i.fornecedor_nome || (i.codigo_fornecedor ? '—' : '—')}
                  {i.codigo_fornecedor && (
                    <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>cod. {i.codigo_fornecedor}</div>
                  )}
                </td>
                <td style={{ ...td, textAlign: 'right', whiteSpace: 'nowrap' }}>
                  {i.preco_embalagem ? formatMoeda(Number(i.preco_embalagem)) : '—'}
                  {i.preco_unitario && (
                    <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                      {formatMoedaSemSimbolo(Number(i.preco_unitario), 4)} / un
                    </div>
                  )}
                </td>
                <td style={td}>{i.ncm || '—'}</td>
                <td style={{ ...td, fontSize: 11, color: 'var(--text-muted)' }}>
                  {formatDataSP(i.criado_em)}<br />{i.criado_por_nome || '—'}
                </td>
                <td style={{ ...td, textAlign: 'center', whiteSpace: 'nowrap' }}>
                  {i.status === 'ativo' && (
                    <>
                      <button type="button" title="Duplicar — abre um item novo com estes dados"
                        onClick={(e) => { e.stopPropagation(); abrirDuplicata(i); }}
                        style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--primary)', padding: 2 }}>
                        <Copy size={15} />
                      </button>
                      {i.foto_url && (
                        <button type="button" title="Remover a foto" onClick={(e) => { e.stopPropagation(); void tirarFoto(i); }}
                          style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 2 }}>
                          <X size={15} />
                        </button>
                      )}
                      <button type="button" title="Descartar" onClick={(e) => { e.stopPropagation(); void descartar(i); }}
                        style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: '#ef4444', padding: 2 }}>
                        <Trash2 size={15} />
                      </button>
                    </>
                  )}
                  {i.status === 'descartado' && (
                    <button type="button" title="Voltar pra prateleira" onClick={(e) => { e.stopPropagation(); void reativar(i); }}
                      style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--primary)', padding: 2 }}>
                      <RotateCcw size={15} />
                    </button>
                  )}
                  {i.status === 'comprado' && <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>—</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {vista === 'ativo' && itens.length > 0 && (
        <p style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 8 }}>
          Selecione uma linha e tecle <strong>Ctrl+V</strong> para colar a foto do item.
        </p>
      )}

      {modalAberto && (
        <Modal
          aberto
          titulo={
            duplicandoDe ? `Novo item — copia de ${duplicandoDe.codigo}`
              : editando ? `Editar ${editando.codigo}`
                : 'Novo item na prateleira'
          }
          largura={620}
          onFechar={fecharModal}
          rodape={
            <>
              {/* Duplicar mora no rodape da EDICAO, que e' de onde o pedido saiu (print de
                  24/08/2026, modal do NP00005 aberto). A porta gemea fica na coluna ACOES da
                  lista, pra nao ser preciso abrir o item antes ([DA-470]: em ferramenta de uso
                  raro, o custo de esquecer que ela existe supera o de ve-la sobrando). */}
              {editando && (
                <button type="button" className="btn-secundario" onClick={() => void duplicarDoModal()}
                  style={{ marginRight: 'auto', display: 'inline-flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap' }}
                  title="Criar um item NOVO com estes mesmos dados">
                  <Copy size={14} /> Duplicar
                </button>
              )}
              <button type="button" onClick={fecharModal}
                style={{ padding: '8px 16px', background: 'var(--panel-bg)', border: '1px solid var(--border)', borderRadius: 6, color: 'var(--page-fg)', cursor: 'pointer' }}>
                Cancelar
              </button>
              {/* Salvar espera a copia da foto terminar — senao o item nasce sem ela e o download
                  pousa num modal ja fechado. O botao DIZ por que esta cinza. */}
              <button type="button" className="btn-primario" onClick={() => void salvar()}
                disabled={salvando || copiandoFoto || !form.descricao.trim()}>
                {salvando ? 'Salvando...' : copiandoFoto ? 'Copiando a foto...' : 'Salvar'}
              </button>
            </>
          }
        >
          <div style={{ display: 'grid', gap: 12 }}>
            {/* O que o Salvar vai fazer, escrito: sem isto a tela ficaria identica a uma edicao,
                e "duplicar" poderia ler como "estou mexendo no NP00005". Neutro de proposito —
                o amarelo deste modal ja e' do aviso de sosia. */}
            {duplicandoDe && (
              <div style={{
                background: 'var(--page-bg)', border: '1px solid var(--border)', borderRadius: 6,
                padding: '8px 10px', fontSize: 12, display: 'flex', alignItems: 'center', gap: 8,
              }}>
                <Copy size={14} style={{ flexShrink: 0, color: 'var(--primary)' }} />
                <span>
                  Copia de <strong>{duplicandoDe.codigo}</strong> — ao salvar nasce um item{' '}
                  <strong>novo</strong>, com codigo proprio. O {duplicandoDe.codigo} continua como esta.
                </span>
              </div>
            )}
            {/* PRIMEIRO de tudo, por pedido dele (24/08/2026) — e' a ordem em que ele preenche: ele
                esta olhando a lista de um fornecedor e copia dali o codigo e a descricao.
                Os dois sao OPCIONAIS: o caso de origem desta prateleira e o item que ainda nao tem
                fornecedor nenhum. ⛔ Isto NAO cria identidade — quem a da e a aposentadoria, com o
                fornecedor DAQUELA nota, que pode nem ser este ([DA-606] p.3). */}
            {/* A FOTO fica na LATERAL destes tres campos, nao embaixo (pedido dele, 24/08/2026):
                empilhada, ela sozinha empurrava o modal alem da tela e obrigava a rolar pra ver os
                campos de baixo. Ao lado, ela ocupa uma altura que ja existia. `alignItems:stretch`
                pra a moldura da foto acompanhar a altura das duas linhas — inclusive quando o aviso
                "informe a descricao" aparece e a coluna da esquerda cresce. */}
            <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) 168px', gap: 12, alignItems: 'stretch' }}>
              <div style={{ display: 'grid', gap: 12, minWidth: 0, alignContent: 'start' }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,2fr) minmax(0,1fr)', gap: 12 }}>
                  <div>
                    <label style={{ fontSize: 12, color: 'var(--text-muted)' }}>Fornecedor <span style={{ opacity: 0.6 }}>(opcional)</span></label>
                    <ComboBox opcoes={opcoesFornecedor} value={form.fornecedor_id === '' ? null : form.fornecedor_id}
                      onChange={(v) => setForm({ ...form, fornecedor_id: v === '' || v == null ? '' : Number(v) })}
                      placeholder="Onde voce viu..." />
                  </div>
                  <div>
                    <label style={{ fontSize: 12, color: 'var(--text-muted)' }}>Codigo dele</label>
                    <UpperInput value={form.codigo_fornecedor} onChange={(v) => setForm({ ...form, codigo_fornecedor: v })}
                      placeholder="19543" style={{ width: '100%', border: '1px solid var(--border)', borderRadius: 6 }} />
                  </div>
                </div>

                <div>
                  <label style={{ fontSize: 12, color: 'var(--text-muted)' }}>Descricao *</label>
                  <UpperInput
                    value={form.descricao}
                    onChange={(v) => setForm({ ...form, descricao: v })}
                    onBlur={(e) => void conferirParecidos(e.currentTarget.value)}
                    placeholder="EX: ACIDO MURIATICO 1L"
                    style={{ width: '100%', border: '1px solid var(--border)', borderRadius: 6 }}
                  />
                  {/* Botao desabilitado DIZ por que — beco sem saida nao e interface limpa. */}
                  {!form.descricao.trim() && (
                    <div style={{ fontSize: 11, color: '#f97316', marginTop: 4 }}>
                      Informe a descricao para poder salvar.
                    </div>
                  )}
                </div>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                <label style={{ fontSize: 12, color: 'var(--text-muted)' }}>Foto</label>
                <input ref={arquivoRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden"
                  onChange={(e) => { aceitarFoto(e.target.files?.[0]); e.target.value = ''; }} />
                {/* A lixeira sobrepoe o canto: numa coluna de 168px nao ha largura pra um botao ao
                    lado sem espremer a foto. */}
                <div style={{ position: 'relative', flex: 1, minHeight: 104, display: 'flex' }}>
                  <button
                    type="button"
                    onClick={() => arquivoRef.current?.click()}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={(e) => { e.preventDefault(); aceitarFoto(e.dataTransfer?.files?.[0]); }}
                    className="w-full border-2 border-dashed rounded-lg hover:bg-black/5 flex flex-col items-center justify-center gap-1"
                    style={{ borderColor: 'var(--input-border, rgba(0,0,0,0.2))', padding: 6, flex: 1, minWidth: 0 }}
                    title="Cole com Ctrl+V, arraste um arquivo pra ca ou clique pra escolher"
                  >
                    {fotoMostrando ? (
                      // ⚠️ Teto em PX, nao em `100%`: a altura da moldura e' `auto` (sai do
                      // conteudo), entao percentual nao resolve e a imagem passa a MANDAR na
                      // altura da linha — medido em prod, um frasco de 600px esticou a linha pra
                      // 171px onde os campos pedem 120, e era a foto empurrando o modal, nao eles.
                      <img src={fotoMostrando} alt="" style={{ maxHeight: 96, maxWidth: '100%', objectFit: 'contain', borderRadius: 6 }} />
                    ) : copiandoFoto ? (
                      // A copia da foto e' o unico passo do Duplicar que demora — sem dizer isso,
                      // a moldura vazia le como "a foto nao veio" e ele cola outra por cima.
                      <span style={{ fontSize: 12, fontWeight: 500, textAlign: 'center', lineHeight: 1.25 }}>
                        Copiando<br />a foto...
                      </span>
                    ) : (
                      <>
                        <Upload size={16} />
                        {/* Texto curto porque a coluna e estreita — o longo quebrava em 4 linhas e
                            estourava a moldura. O detalhe completo vive no `title`. */}
                        <span style={{ fontSize: 12, fontWeight: 500, textAlign: 'center', lineHeight: 1.25 }}>Ctrl+V,<br />arraste ou clique</span>
                      </>
                    )}
                  </button>
                  {fotoMostrando && (
                    <button type="button" className="btn-secundario" title="Tirar a foto"
                      style={{ position: 'absolute', top: 4, right: 4, padding: '3px 6px' }}
                      onClick={() => { setFotoNova(null); setFotoApagar(true); }}>
                      <Trash2 size={13} />
                    </button>
                  )}
                </div>
              </div>
            </div>

            {parecidos.length > 0 && (
              <div style={{ background: '#fde68a', color: '#000', borderRadius: 6, padding: '8px 10px', fontSize: 12 }}>
                <strong>Ja existe algo parecido</strong> — confira antes de cadastrar de novo:
                <ul style={{ margin: '6px 0 0 16px', listStyle: 'disc' }}>
                  {parecidos.map((p) => (
                    <li key={`${p.onde}-${p.id}`}>
                      <strong>{p.onde === 'produto' ? 'Produto' : p.onde === 'catalogo' ? 'Catalogo' : 'Novo Produto'}</strong>
                      {' '}{p.codigo || '—'} · {p.descricao}{p.empresa ? ` (${p.empresa})` : ''}
                    </li>
                  ))}
                </ul>
                <div style={{ marginTop: 6 }}>Se for outro item mesmo, pode salvar — isto e so um aviso.</div>
              </div>
            )}

            {/* Duas colunas, nao tres: a do meio era a Unidade ([DA-608] p.4). `minmax(0,...)` nas
                DUAS de proposito — a da Marca leva o botao `+` e sem o piso zerado ela nao cederia
                largura pra NCM ([DA-602]). */}
            <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,2fr) minmax(0,1fr)', gap: 12 }}>
              <div>
                <label style={{ fontSize: 12, color: 'var(--text-muted)' }}>Marca</label>
                <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <ComboBox opcoes={opcoesMarca} value={form.marca || null}
                      onChange={(v) => setForm({ ...form, marca: v === '' || v == null ? '' : String(v) })}
                      placeholder="Escolha a marca..." />
                  </div>
                  <button type="button" className="btn-secundario" onClick={() => setMostrarNovaMarca(true)}
                    style={{ padding: '6px 10px', fontSize: 12, whiteSpace: 'nowrap' }} title="Cadastrar uma marca nova">
                    <Plus size={12} />
                  </button>
                </div>
              </div>
              {/* ⛔ Aqui havia um campo "Unidade", tirado a pedido dele em 24/08/2026: *"Se ja tem
                  esta unidade, nao precisa da outra"* ([DA-608] p.4). Ele e a Embalagem liam a
                  MESMA tabela (`unidades_medida`) e no caso comum — item avulso — pediam a mesma
                  escolha duas vezes; e quem viaja pro Catalogo na aposentadoria e' a embalagem,
                  nunca a unidade. Nao repor sem ler a p.4: a pergunta que ele fez foi "por que vou
                  escolher Unidade duas vezes?", e ela nao tem resposta boa. */}
              <div>
                <label style={{ fontSize: 12, color: 'var(--text-muted)' }}>NCM</label>
                <input value={form.ncm} onChange={(e) => setForm({ ...form, ncm: e.target.value.replace(/\D/g, '').slice(0, 8) })}
                  style={{ width: '100%', border: '1px solid var(--border)', borderRadius: 6 }} />
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) minmax(0,1fr)', gap: 12 }}>
              <div>
                <label style={{ fontSize: 12, color: 'var(--text-muted)' }}>Embalagem</label>
                <ComboBox opcoes={opcoesUnidade(form.embalagem)} value={form.embalagem || null}
                  onChange={(v) => setForm({ ...form, embalagem: v === '' || v == null ? '' : String(v) })}
                  placeholder="Escolha a embalagem..." />
              </div>
              <div>
                <label style={{ fontSize: 12, color: 'var(--text-muted)' }}>Qtd. na embalagem</label>
                {/* Formata no `onBlur` como o campo de dinheiro ao lado: sem isso "1000" fica
                    "1000" na tela e "1.000" depois de reabrir o item, e as duas grafias liam
                    numeros diferentes ([DA-608] p.2). */}
                <input value={form.quantidade_embalagem}
                  onChange={(e) => setForm({ ...form, quantidade_embalagem: e.target.value.replace(/[^\d.,]/g, '') })}
                  onBlur={(e) => { const n = parseQtd(e.target.value); setForm((f) => ({ ...f, quantidade_embalagem: n != null ? fmtQtd(n) : '' })); }}
                  placeholder="100" style={{ width: '100%', border: '1px solid var(--border)', borderRadius: 6, textAlign: 'right' }} />
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) minmax(0,1fr)', gap: 12 }}>
              <div>
                <label style={{ fontSize: 12, color: 'var(--text-muted)' }}>Valor da embalagem (R$)</label>
                <input value={form.preco_embalagem}
                  onChange={(e) => setForm({ ...form, preco_embalagem: e.target.value })}
                  onBlur={(e) => { const n = parseMoeda(e.target.value); setForm((f) => ({ ...f, preco_embalagem: n > 0 ? formatMoedaSemSimbolo(n, 2) : '' })); }}
                  placeholder="60,00" style={{ width: '100%', border: '1px solid var(--border)', borderRadius: 6, textAlign: 'right' }} />
              </div>
              <div>
                <label style={{ fontSize: 12, color: 'var(--text-muted)' }}>Valor unitario</label>
                {/* So-leitura: e o resultado da divisao, nao um segundo lugar pra digitar o mesmo
                    numero. `<div>` nao herda o `!important` de cor dos inputs — por isso a cor sai
                    de `var(--input-fg)` explicito. */}
                <div style={{
                  width: '100%', border: '1px solid var(--border)', borderRadius: 6,
                  padding: '8px 14px', textAlign: 'right', background: 'var(--page-bg)',
                  color: 'var(--input-fg)', minHeight: 38,
                }}>
                  {unitarioPrevia != null ? `${formatMoedaSemSimbolo(unitarioPrevia, 4)} / un` : '—'}
                </div>
              </div>
            </div>

            <div>
              <label style={{ fontSize: 12, color: 'var(--text-muted)' }}>Observacao</label>
              <textarea value={form.observacao} onChange={(e) => setForm({ ...form, observacao: e.target.value })}
                rows={2} placeholder="Onde voce viu, quem indicou, o que precisa conferir..."
                style={{ width: '100%', border: '1px solid var(--border)', borderRadius: 6, resize: 'vertical' }} />
            </div>

            <p style={{ fontSize: 11, color: 'var(--text-muted)', margin: 0 }}>
              O codigo <strong>NP</strong> e gerado pelo sistema ao salvar. A foto tambem da pra colar depois,
              na lista, selecionando a linha e teclando Ctrl+V.
            </p>
          </div>

          {mostrarNovaMarca && (
            <ModalCadastroRapido
              titulo="Nova Marca"
              endpoint="/api/marcas"
              campos={[{ key: 'nome', label: 'Nome', obrigatorio: true }]}
              onFechar={() => setMostrarNovaMarca(false)}
              onSalvo={(novo: { id: number; nome: string }) => {
                setMarcas((prev) => [...prev, novo].sort((a, b) => a.nome.localeCompare(b.nome)));
                setForm((f) => ({ ...f, marca: novo.nome }));
                setMostrarNovaMarca(false);
              }}
            />
          )}
        </Modal>
      )}
    </div>
  );
}
